import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, GitMerge, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { api, errorMessage } from "../../lib/api";

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const Row = ({ d, i, checked, onCheck, onMerge, busy, navigate }) => (
  <li className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-[#f5f8fd] px-3 py-2 text-sm" data-testid={`duplicate-client-${i}`}>
    <label className="flex min-w-0 items-center gap-2">
      <input type="checkbox" checked={checked} onChange={(e) => onCheck(e.target.checked)} data-testid={`duplicate-check-${i}`} className="h-4 w-4 accent-[#0f5ef7]" />
      <button type="button" onClick={() => navigate(`/clientes/${encodeURIComponent(d.borrower_id)}`)} className="text-left font-semibold text-[#182235] hover:underline" data-testid={`duplicate-link-${i}`}>
        {d.name} <span className="font-mono text-xs text-[#5b667a]">{d.borrower_id}</span>
      </button>
      <span className="text-[#5b667a]">→</span>
      <span className="font-semibold text-[#0f5ef7]" data-testid={`duplicate-target-${i}`}>{d.target_name}</span>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${d.kind === "exacto" ? "bg-[#e7f6ec] text-[#067647]" : "bg-[#fff4e5] text-[#b54708]"}`} data-testid={`duplicate-kind-${i}`}>
        {{ exacto: "Mismo nombre", parcial: "Nombre incompleto", espacios: "Igual sin espacios" }[d.kind] || "Parecido"}
      </span>
    </label>
    <span className="flex items-center gap-3 text-xs text-[#5b667a]">
      {plural(d.files, "archivo", "archivos")}
      <button type="button" disabled={busy} onClick={onMerge} className="tr-btn-light px-2.5 py-1.5 text-xs" data-testid={`duplicate-merge-${i}`}>
        <GitMerge className="h-3.5 w-3.5" /> Fusionar con {d.target_name}
      </button>
    </span>
  </li>
);

export const DuplicateClients = ({ onChanged }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [checked, setChecked] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/client-admin/duplicates").then((r) => {
    setItems(r.data);
    setChecked(Object.fromEntries(r.data.map((d) => [d.borrower_id, d.kind === "exacto"])));
  }).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const run = async () => {
    const list = confirm;
    setConfirm(null);
    setBusy(true);
    try {
      const r = (await api.post("/client-admin/merge-many", { pairs: list.map((d) => ({ source_borrower_id: d.borrower_id, target_borrower_id: d.target_borrower_id })) }, { timeout: 300000 })).data;
      toast.success(`${plural(r.fusionados, "cliente fusionado", "clientes fusionados")} · ${plural(r.files, "archivo movido", "archivos movidos")}`);
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "No se pudo fusionar, intenta de nuevo"));
    }
    setBusy(false);
    load();
  };
  if (!items?.length) return null;
  const selected = items.filter((d) => checked[d.borrower_id]);
  return (
    <section className="tr-card space-y-3 p-4 sm:p-5" data-testid="duplicate-clients-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-base font-bold text-[#182235]" data-testid="duplicate-clients-title">
          <Copy className="h-4 w-4 text-[#0f5ef7]" /> Posibles duplicados ({items.length})
        </h2>
        <button type="button" disabled={busy || !selected.length} onClick={() => setConfirm(selected)} className="tr-btn-light px-3 py-2 text-xs" data-testid="duplicate-merge-all-button">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />} Fusionar todos los visibles ({selected.length})
        </button>
      </div>
      <p className="text-xs text-[#5b667a]">Clientes creados por la carga masiva que parecen ser un cliente del archivo de Excel. “Mismo nombre” viene marcado; revisa los “Parecido” antes de marcarlos.</p>
      <ul className="max-h-[28rem] space-y-1.5 overflow-y-auto">
        {items.map((d, i) => (
          <Row key={d.borrower_id} d={d} i={i} busy={busy} navigate={navigate} checked={!!checked[d.borrower_id]}
            onCheck={(v) => setChecked((c) => ({ ...c, [d.borrower_id]: v }))} onMerge={() => setConfirm([d])} />
        ))}
      </ul>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="duplicate-merge-dialog" className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Fusionar {plural(confirm?.length || 0, "cliente", "clientes")}?</AlertDialogTitle>
            <AlertDialogDescription>Sus archivos y créditos pasarán al cliente de la derecha y el duplicado se eliminará.</AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-80 space-y-1.5 overflow-y-auto text-sm" data-testid="duplicate-merge-list">
            {confirm?.map((d, i) => (
              <li key={d.borrower_id} className="rounded-lg bg-[#f5f8fd] px-3 py-2" data-testid={`duplicate-merge-item-${i}`}>
                <span className="font-semibold text-[#182235]">{d.name}</span> <span className="text-[#5b667a]">→</span> <span className="font-semibold text-[#0f5ef7]">{d.target_name}</span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="duplicate-merge-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="duplicate-merge-confirm">Sí, fusionar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
